import { FormProvider, useForm, type FieldErrors } from 'react-hook-form';
import { zodResolver } from './../../../../../node_modules/@hookform/resolvers/zod/src/zod';

import PhoneNumberField from './filed/PhoneNumberField';
import EmailField from './filed/EmailField';
import { loginSchema, type LoginFormValues } from './schema/LoginSchema';
import PasswordField from './filed/PasswordField';
import { Button } from '@/components/ui/button';
import LoginMethodToggle from './LoginMethodToggle';
import { useLoginMutation } from '@/service/api/auth';
import { getPreferredLanguage } from '@/shared/utils/getPreferredLanguage';
import { useState } from 'react';
import { sha256Encrypt } from '@/shared/lib/crypto';
import { formatPhoneNumberForServer } from '@/shared/utils/formatPhoneNumberForServer';
import { useAlertDialog } from '@/shared/context/AlertDialogContext';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/shared/provider/AuthProvider';

const LoginForm = () => {
	const form = useForm<LoginFormValues>({
		resolver: zodResolver(loginSchema),
		defaultValues: {
			grantType: 'phone',
			phone: '',
			email: '',
			password: '',
		},
		mode: 'onChange',
	});
	const [selectedCountry, setSelectedCountry] = useState<{
		code: string;
		dial: string;
	}>({ code: 'KH', dial: '+855' });

	// hooks
	const { showAlert, closeAlert } = useAlertDialog();
	const { onLogin } = useAuth();
	const navigate = useNavigate();

	// 변수
	const grantType = form.watch('grantType');
	const language = getPreferredLanguage();
	const redirectMarketUrl = 'https://angkorlife.onelink.me/MIbi';

	// API
	const { mutate: loginMutate } = useLoginMutation();

	// 함수
	const redirectToUrl = () => {
		window.location.href = redirectMarketUrl;
	};

	const onSubmit = (data: LoginFormValues) => {
		const fullPhoneNumber = formatPhoneNumberForServer(
			selectedCountry.dial,
			data.phone
		);

		const request = {
			grantType: data.grantType,
			id: data.grantType === 'phone' ? fullPhoneNumber : (data.email as string),
			password: sha256Encrypt(data.password),
			language,
		};

		loginMutate(request, {
			onSuccess: response => {
				onLogin(response);
				navigate('/', { replace: true });
			},
			onError: error => {
				if (error.response?.data?.code === '4002001') {
					showAlert({
						variant: 'error',
						title:
							error.response?.data.message ||
							error.message ||
							'An unexpected error occurred. Please try again later.',
						actionText: 'Close',
						onConfirm: () => {
							redirectToUrl();
						},
					});
				} else {
					showAlert({
						variant: 'error',
						title:
							error.response?.data.message ||
							error.message ||
							'An unexpected error occurred. Please try again later.',
						actionText: 'Close',
						onConfirm: () => {
							closeAlert();
						},
					});
				}
			},
		});
	};

	// const onError = (errors: FieldErrors<LoginFormValues>) => {
	// 	console.log('erros', errors);
	// };

	return (
		<div className="mb-[26px]">
			<FormProvider {...form}>
				<form onSubmit={form.handleSubmit(onSubmit)}>
					{grantType === 'phone' ? (
						<PhoneNumberField
							selectedCountry={selectedCountry}
							onClick={(val: { code: string; dial: string }) =>
								setSelectedCountry(val)
							}
						/>
					) : (
						<EmailField />
					)}
					<PasswordField />
					<Button variant="primary" size="full" className="text-base">
						Log in
					</Button>

					{/* Divider for OR */}
					<div className="relative my-6">
						<div className="absolute inset-0 flex items-center">
							<div className="w-full border-t border-gray-05" />
						</div>
						<div className="relative flex justify-center text-sm">
							<span className="px-2 bg-white text-label-secondary">OR</span>
						</div>
					</div>

					{/* Login Method Toggle */}
					<LoginMethodToggle />
				</form>
			</FormProvider>
		</div>
	);
};

export default LoginForm;
